import { useMemo, useState } from 'react';
import { format, addDays, subDays, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { RacquetSportId } from '@court-sync/shared';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { usePublicOrgCourtHireGrid } from '@/queries/publicOrgProfile';
import type { PublicOrgSport } from '@/services/publicOrg';
import { SportIconTile } from '@/components/sport/SportIcons';
import { getSportLabel } from '@/lib/sportPresentation';

const SLOT_STATE_CLASSES: Record<string, string> = {
  available: 'bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/40 cursor-pointer',
  booked: 'bg-rose-100 dark:bg-rose-900/40 cursor-not-allowed',
  unavailable: 'bg-muted cursor-not-allowed',
};

interface OrgCourtHireGridProps {
  orgSlug: string;
  sports: PublicOrgSport[];
  locationId?: string;
  onSlotSelect?: (courtId: string, date: string, startTime: string, endTime: string) => void;
}

export function OrgCourtHireGrid({
  orgSlug,
  sports,
  locationId,
  onSlotSelect,
}: OrgCourtHireGridProps) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate] = useState(today);
  // Default to all courts — sport filter is opt-in (matches dedicated court-hire page).
  const [sportId, setSportId] = useState<RacquetSportId | undefined>(undefined);

  const { grid, isLoading, error } = usePublicOrgCourtHireGrid(
    orgSlug,
    date,
    sportId,
    locationId,
    true
  );

  const hourLabels = useMemo(() => {
    if (!grid) return [] as string[];
    const startH = parseInt(grid.operatingWindow.startTime.split(':')[0] ?? '0', 10);
    const endH = parseInt(grid.operatingWindow.endTime.split(':')[0] ?? '0', 10);
    const labels: string[] = [];
    for (let h = startH; h < endH; h += 1) {
      labels.push(`${String(h).padStart(2, '0')}:00`);
    }
    return labels;
  }, [grid]);

  const parsedDate = parseISO(date);

  return (
    <section aria-labelledby="court-hire-heading" className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 id="court-hire-heading" className="text-2xl font-bold text-foreground">
          Book a court
        </h2>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Previous day"
            onClick={() => setDate(format(subDays(parsedDate, 1), 'yyyy-MM-dd'))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <time dateTime={date} className="min-w-[10rem] text-center text-sm font-medium">
            {format(parsedDate, 'EEE, d MMM yyyy')}
          </time>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Next day"
            onClick={() => setDate(format(addDays(parsedDate, 1), 'yyyy-MM-dd'))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {sports.length > 1 ? (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Sport">
          <button
            type="button"
            onClick={() => setSportId(undefined)}
            className={cn(
              'inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
              sportId == null
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-card text-muted-foreground hover:bg-muted'
            )}
          >
            All sports
          </button>
          {sports.map((sport) => {
            const label = getSportLabel(sport);
            const active = sportId === sport.sportId;
            return (
              <button
                key={sport.sportId}
                type="button"
                onClick={() => setSportId(sport.sportId)}
                className={cn(
                  'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-card text-muted-foreground hover:bg-muted'
                )}
              >
                <SportIconTile sportId={sport.sportId} className="h-5 w-5" />
                {label}
              </button>
            );
          })}
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
        </div>
      ) : error ? (
        <p className="text-sm text-muted-foreground">{error}</p>
      ) : !grid || grid.courts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No courts available for this date.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-xs text-muted-foreground">
                <th className="p-2 text-left font-medium">Court</th>
                {hourLabels.map((label) => (
                  <th key={label} className="border-l border-border p-2 text-center font-medium">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.courts.map((court) => (
                <tr key={court.id} className="border-b border-border last:border-b-0">
                  <td className="p-2 font-medium">{court.name}</td>
                  {court.slots.map((slot) => (
                    <td key={`${court.id}-${slot.startTime}`} className="border-l border-border p-0.5">
                      <button
                        type="button"
                        disabled={slot.state !== 'available'}
                        title={`${slot.startTime} – ${slot.endTime}`}
                        className={cn(
                          'h-8 w-full rounded text-[10px]',
                          SLOT_STATE_CLASSES[slot.state]
                        )}
                        onClick={() =>
                          slot.state === 'available' &&
                          onSlotSelect?.(court.id, date, slot.startTime, slot.endTime)
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-emerald-100 dark:bg-emerald-900/40" aria-hidden />
          Available
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-rose-100 dark:bg-rose-900/40" aria-hidden />
          Booked
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-muted" aria-hidden />
          Unavailable
        </span>
      </div>
    </section>
  );
}
